import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { canAccessFeature, isCompanyAdmin } from '../utils/planAccess';
import { fetchCompanyUsers, formatUserLabel } from '../utils/companyUsers';
import { downloadTextFile, escapeCsvValue } from '../utils/csv';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import EndOfDayHandoutsSection from '../components/EndOfDayHandoutsSection';
import { useConfirm } from '../context/ConfirmContext';
import {
  formatMinutesAsHours,
  getDayBreakdown,
  getHourTypeLabel,
  orderedTypeSlugs,
  sumTotals
} from '../utils/classifiedHoursDisplay';
import { formatDateTime, localeToBcp47 } from '../utils/locale';
import { aggregateTaskTimeEntries } from '../utils/aggregateTaskTimeEntries';
import { segmentNetMinutes } from '../utils/taskCheckInTime';
import './Tasks.css';
import './TimeReport.css';

const formatTime = (minutes) => {
  if (!minutes && minutes !== 0) return '—';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

const sessionKindLabel = (t, sessionKind) =>
  sessionKind === 'jour' ? t('sessionKindJour') : t('sessionKindOrdinary');

const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

const fromDateTimeLocalValue = (value) => {
  if (!value) return null;
  return new Date(value).toISOString();
};

const dateKey = (value) => {
  const date = new Date(value);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const yesterdayKey = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return dateKey(date);
};

const monthRange = (month) => {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const start = `${year}-${String(monthIndex + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  const end = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { startDate: start, endDate: end };
};

const WEEKDAY_KEYS = ['weekdaySun', 'weekdayMon', 'weekdayTue', 'weekdayWed', 'weekdayThu', 'weekdayFri', 'weekdaySat'];

const editableTaskTimeSegments = (row, isLockedForUser) =>
  (row.segments || [row]).filter(
    (segment) =>
      segment.check_out_time
      && !isLockedForUser(segment.user_id, dateKey(segment.check_in_time))
  );

const firstLockedTaskTimeDay = (row, isLockedForUser) => {
  for (const segment of row.segments || [row]) {
    const day = dateKey(segment.check_in_time);
    if (isLockedForUser(segment.user_id, day)) {
      return day;
    }
  }
  return null;
};

const AttendanceCalendar = ({
  sessions,
  attestedDates,
  month,
  onMonthChange,
  selectedDay,
  onSelectDay
}) => {
  const { t, i18n } = useTranslation(['timeReport', 'common', 'nav']);
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const markedDays = new Set(sessions.map((session) => dateKey(session.check_in_at)));
  const attestedSet = new Set(attestedDates);
  const cells = Array.from({ length: firstDay.getDay() + daysInMonth }, (_, index) => {
    const day = index - firstDay.getDay() + 1;
    return day > 0 ? new Date(month.getFullYear(), month.getMonth(), day) : null;
  });
  const shiftMonth = (offset) => onMonthChange(new Date(month.getFullYear(), month.getMonth() + offset, 1));

  return (
    <section className="card attendance-calendar">
      <div className="attendance-calendar-header">
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => shiftMonth(-1)}>{t('previousMonth')}</button>
        <h2>{month.toLocaleString(localeToBcp47(i18n.language), { month: 'long', year: 'numeric' })}</h2>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => shiftMonth(1)}>{t('nextMonth')}</button>
      </div>
      <div className="attendance-calendar-weekdays">
        {WEEKDAY_KEYS.map((key) => <span key={key}>{t(key)}</span>)}
      </div>
      <div className="attendance-calendar-grid">
        {cells.map((date, index) => {
          if (!date) return <span key={`empty-${index}`} />;
          const key = dateKey(date);
          const classes = [
            'attendance-calendar-day',
            markedDays.has(key) ? 'has-attendance' : '',
            attestedSet.has(key) ? 'is-attested' : '',
            selectedDay === key ? 'selected' : ''
          ].filter(Boolean).join(' ');
          return (
            <button
              key={key}
              type="button"
              className={classes}
              onClick={() => onSelectDay(key)}
              title={attestedSet.has(key) ? t('attestedTooltip') : undefined}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </section>
  );
};

const TaskTimeCards = ({
  entries,
  isAdmin,
  isLockedForUser,
  onEdit,
  onUnlock,
  submitting
}) => {
  const { t } = useTranslation(['timeReport', 'common', 'nav']);

  return (
    <div className="time-report-cards-mobile">
      {entries.map((entry) => {
        const isCompleted = Boolean(entry.check_out_time);
        const editable = editableTaskTimeSegments(entry, isLockedForUser);
        const unlockDay = firstLockedTaskTimeDay(entry, isLockedForUser);
        return (
          <div key={entry.rowKey} className="time-report-mobile-card">
            <div className="time-report-mobile-card-body">
              <div className="time-report-mobile-card-top">
                <h3 className="time-report-mobile-card-title">
                  <Link to={`/tasks/${entry.task_id}`}>{entry.task_title}</Link>
                </h3>
                {!isCompleted ? (
                  <span className="time-report-status-pill time-report-status-pill--active">{t('active')}</span>
                ) : unlockDay ? (
                  <span className="time-report-status-pill time-report-status-pill--attested">{t('attested')}</span>
                ) : null}
              </div>
              {isAdmin && <p className="time-report-mobile-card-meta">{t('columnUser')}: {entry.user_name}</p>}
              <p className="time-report-mobile-card-meta">
                {t('checkInLabel', { datetime: formatDateTime(entry.check_in_time) || '—' })}
              </p>
              <p className="time-report-mobile-card-meta">
                {t('checkOutLabel', {
                  datetime: entry.check_out_time ? formatDateTime(entry.check_out_time) : t('active')
                })}
              </p>
              <p className="time-report-mobile-card-meta">{t('timeSpent', { time: formatTime(entry.time_spent) })}</p>
              {entry.segmentCount > 1 && (
                <p className="time-report-mobile-card-meta">
                  {t('segmentCount', { count: entry.segmentCount })}
                </p>
              )}
              {entry.lunch_minutes > 0 && (
                <p className="time-report-mobile-card-meta">{t('lunchMinutes', { minutes: entry.lunch_minutes })}</p>
              )}
              {entry.summary && <p className="time-report-mobile-card-meta">{entry.summary}</p>}
              <div className="time-report-mobile-card-actions">
                {unlockDay && isAdmin && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => onUnlock(entry.user_id, unlockDay)}
                    disabled={submitting}
                  >
                    {t('unlock')}
                  </button>
                )}
                {editable.length > 0 ? (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => onEdit(entry)}>
                    {t('edit')}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AttendanceCards = ({
  entries,
  isAdmin,
  isLockedForUser,
  onEdit,
  onUnlock,
  submitting
}) => {
  const { t } = useTranslation(['timeReport', 'common', 'nav']);

  return (
    <div className="time-report-cards-mobile">
      {entries.map((entry) => {
        const locked = isLockedForUser(entry.user_id, dateKey(entry.check_in_at));
        return (
          <div key={entry.id} className="time-report-mobile-card">
            <div className="time-report-mobile-card-body">
              <div className="time-report-mobile-card-top">
                <h3 className="time-report-mobile-card-title">
                  {isAdmin ? entry.user_name : (formatDateTime(entry.check_in_at) || t('dayAttendanceTitle'))}
                </h3>
                {!entry.check_out_at ? (
                  <span className="time-report-status-pill time-report-status-pill--active">{t('active')}</span>
                ) : locked ? (
                  <span className="time-report-status-pill time-report-status-pill--attested">{t('attested')}</span>
                ) : null}
              </div>
              <div className="time-report-mobile-card-grid">
                <div>
                  <span className="time-report-mobile-card-label">{t('columnSessionKind')}</span>
                  <span>{sessionKindLabel(t, entry.session_kind)}</span>
                </div>
                <div>
                  <span className="time-report-mobile-card-label">{t('columnCheckIn')}</span>
                  <span>{formatDateTime(entry.check_in_at) || '—'}</span>
                </div>
                <div>
                  <span className="time-report-mobile-card-label">{t('columnCheckOut')}</span>
                  <span>{entry.check_out_at ? formatDateTime(entry.check_out_at) : '—'}</span>
                </div>
                <div>
                  <span className="time-report-mobile-card-label">{t('columnDuration')}</span>
                  <span>{formatTime(entry.duration_minutes)}</span>
                </div>
              </div>
              {entry.lunch_minutes > 0 && (
                <p className="time-report-mobile-card-meta">{t('lunchMinutes', { minutes: entry.lunch_minutes })}</p>
              )}
              {entry.note && <p className="time-report-mobile-card-meta">{entry.note}</p>}
              <div className="time-report-mobile-card-actions">
                {locked && isAdmin ? (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => onUnlock(entry.user_id, dateKey(entry.check_in_at))}
                    disabled={submitting}
                  >
                    {t('unlock')}
                  </button>
                ) : entry.check_out_at && !locked ? (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => onEdit(entry)}>
                    {t('edit')}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AttendanceTable = ({
  entries,
  isAdmin,
  isLockedForUser,
  onEdit,
  onUnlock,
  submitting
}) => {
  const { t } = useTranslation(['timeReport', 'common', 'nav']);

  return (
    <>
      <div className="time-report-table-desktop">
        <div className="table-responsive">
          <table className="time-report-table">
            <thead>
              <tr>
                {isAdmin && <th>{t('columnUser')}</th>}
                <th>{t('columnSessionKind')}</th>
                <th>{t('columnCheckIn')}</th>
                <th>{t('columnCheckOut')}</th>
                <th>{t('columnDuration')}</th>
                <th>{t('columnNote')}</th>
                <th aria-label={t('columnActions')} />
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const locked = isLockedForUser(entry.user_id, dateKey(entry.check_in_at));
                return (
                  <tr key={entry.id}>
                    {isAdmin && <td>{entry.user_name}</td>}
                    <td>{sessionKindLabel(t, entry.session_kind)}</td>
                    <td>{formatDateTime(entry.check_in_at) || '—'}</td>
                    <td>{formatDateTime(entry.check_out_at) || '—'}</td>
                    <td>{formatTime(entry.duration_minutes)}</td>
                    <td className="time-report-summary">
                      {[entry.note, entry.lunch_minutes > 0 ? t('lunchInNote', { minutes: entry.lunch_minutes }) : null]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </td>
                    <td>
                      {!entry.check_out_at ? (
                        <span className="time-report-status-pill time-report-status-pill--active">{t('active')}</span>
                      ) : locked ? (
                        <div className="time-report-lock-actions">
                          <span className="time-report-status-pill time-report-status-pill--attested">{t('attested')}</span>
                          {isAdmin && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => onUnlock(entry.user_id, dateKey(entry.check_in_at))}
                              disabled={submitting}
                            >
                              {t('unlock')}
                            </button>
                          )}
                        </div>
                      ) : (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => onEdit(entry)}>
                          {t('edit')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <AttendanceCards
        entries={entries}
        isAdmin={isAdmin}
        isLockedForUser={isLockedForUser}
        onEdit={onEdit}
        onUnlock={onUnlock}
        submitting={submitting}
      />
    </>
  );
};

const TimeReportEmpty = ({ description }) => {
  const { t } = useTranslation(['timeReport', 'common', 'nav']);

  return <EmptyState icon="schedule" title={t('noSessionsFoundTitle')} description={description} />;
};

const emptyTotalsObject = (typeSlugs) =>
  typeSlugs.reduce((acc, slug) => {
    acc[slug] = 0;
    return acc;
  }, {});

const ClassifiedHoursPanel = ({
  data,
  loading,
  error,
  selectedDay,
  showMultipleUsers
}) => {
  const { t } = useTranslation(['timeReport', 'common', 'nav']);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!data) {
    return null;
  }

  const users = data.users || [];
  const hourTypes = data.hourTypes || [];
  const typeSlugs = orderedTypeSlugs(hourTypes, users);
  const grandTotals = emptyTotalsObject(typeSlugs);

  users.forEach((entry) => {
    typeSlugs.forEach((slug) => {
      grandTotals[slug] = (grandTotals[slug] || 0) + (entry.totalsByType?.[slug] || 0);
    });
  });

  const selectedDayRows = getDayBreakdown(users, selectedDay);

  if (users.length === 0) {
    return (
      <EmptyState
        icon="schedule"
        title={t('noClassifiedHoursTitle')}
        description={t('noClassifiedHoursDescription')}
      />
    );
  }

  return (
    <div className="work-hour-classified-section">
      <p className="time-report-meta">
        Rules timezone: {data.timezone}. Open sessions are excluded from classified totals.
      </p>

      <div className="work-hour-classified-table-desktop table-responsive">
        <table className="work-hour-classified-table">
          <thead>
            <tr>
              {showMultipleUsers && <th>{t('columnUser')}</th>}
              {typeSlugs.map((slug) => (
                <th key={slug}>{getHourTypeLabel(slug, hourTypes)}</th>
              ))}
              <th>{t('periodTotal')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((entry) => (
              <tr key={entry.userId}>
                {showMultipleUsers && <td>{entry.name || t('userFallback', { id: entry.userId })}</td>}
                {typeSlugs.map((slug) => (
                  <td key={`${entry.userId}-${slug}`}>
                    {formatMinutesAsHours(entry.totalsByType?.[slug] || 0)}
                  </td>
                ))}
                <td>{formatMinutesAsHours(sumTotals(entry.totalsByType))}</td>
              </tr>
            ))}
            {showMultipleUsers && users.length > 1 && (
              <tr className="work-hour-classified-total-row">
                <td>{t('periodTotal')}</td>
                {typeSlugs.map((slug) => (
                  <td key={`grand-${slug}`}>{formatMinutesAsHours(grandTotals[slug] || 0)}</td>
                ))}
                <td>{formatMinutesAsHours(sumTotals(grandTotals))}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="work-hour-classified-cards-mobile">
        {users.map((entry) => (
          <div key={entry.userId} className="time-report-mobile-card work-hour-classified-card">
            <div className="time-report-mobile-card-body">
              {showMultipleUsers && (
                <h3 className="time-report-mobile-card-title">{entry.name || t('userFallback', { id: entry.userId })}</h3>
              )}
              <dl>
                {typeSlugs.map((slug) => (
                  <React.Fragment key={`${entry.userId}-${slug}-mobile`}>
                    <dt>{getHourTypeLabel(slug, hourTypes)}</dt>
                    <dd>{formatMinutesAsHours(entry.totalsByType?.[slug] || 0)}</dd>
                  </React.Fragment>
                ))}
                <dt>{t('periodTotal')}</dt>
                <dd>{formatMinutesAsHours(sumTotals(entry.totalsByType))}</dd>
              </dl>
            </div>
          </div>
        ))}
      </div>

      {selectedDayRows.length > 0 && (
        <div className="work-hour-classified-day-card">
          <h3>{t('selectedDayTotal', { date: selectedDay })}</h3>
          {selectedDayRows.map((row) => (
            <div key={`${row.userId}-${selectedDay}`} className="work-hour-classified-card">
              {showMultipleUsers && <strong>{row.name || t('userFallback', { id: row.userId })}</strong>}
              <dl>
                {typeSlugs.map((slug) => (
                  <React.Fragment key={`${row.userId}-${selectedDay}-${slug}`}>
                    <dt>{getHourTypeLabel(slug, hourTypes)}</dt>
                    <dd>{formatMinutesAsHours(row.totalsByType?.[slug] || 0)}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TimeReport = () => {
  const { t } = useTranslation(['timeReport', 'common', 'nav', 'dashboard']);
  const confirm = useConfirm();
  const { user } = useContext(AuthContext);
  const [entries, setEntries] = useState([]);
  const [attendanceEntries, setAttendanceEntries] = useState([]);
  const [attestation, setAttestation] = useState({ attested: [], unlocks: [] });
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);
  const [segmentPickerRow, setSegmentPickerRow] = useState(null);
  const [editForm, setEditForm] = useState({
    check_in_time: '',
    check_out_time: '',
    time_spent: '',
    took_lunch: false,
    lunch_minutes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [attendanceForm, setAttendanceForm] = useState({
    check_in_at: '',
    check_out_at: '',
    note: '',
    took_lunch: false,
    lunch_minutes: ''
  });
  const [handoutsBlockingCheckout, setHandoutsBlockingCheckout] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState(dateKey(new Date()));
  const [attestForm, setAttestForm] = useState({ startDate: '', endDate: yesterdayKey() });
  const [fortnoxPushSummary, setFortnoxPushSummary] = useState(null);
  const [classifiedHours, setClassifiedHours] = useState(null);
  const [classifiedLoading, setClassifiedLoading] = useState(false);
  const [classifiedError, setClassifiedError] = useState('');
  const moreActionsRef = useRef(null);

  const closeMoreActions = useCallback(() => {
    if (moreActionsRef.current) {
      moreActionsRef.current.open = false;
    }
  }, []);

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = isCompanyAdmin(user) && !isSuperAdmin;
  const canExport = isAdmin && canAccessFeature(user, 'exportData');
  const canPushFortnox = isAdmin && canAccessFeature(user, 'customIntegrations');
  const showEodHandoutsOnCheckout = Boolean(
    editingAttendance?.checkout
    && !isSuperAdmin
    && canAccessFeature(user, 'handouts')
    && editingAttendance.user_id === user?.id
  );

  const attestationQueryRange = useMemo(() => {
    const month = monthRange(calendarMonth);
    if (startDate || endDate) {
      const starts = [startDate, month.startDate].filter(Boolean).sort();
      const ends = [endDate, month.endDate, dateKey(new Date())].filter(Boolean).sort();
      return { startDate: starts[0], endDate: ends[ends.length - 1] };
    }
    const start = new Date();
    start.setFullYear(start.getFullYear() - 2);
    return { startDate: dateKey(start), endDate: dateKey(new Date()) };
  }, [startDate, endDate, calendarMonth]);

  const classifiedRange = useMemo(() => {
    if (startDate && endDate) {
      return { startDate, endDate };
    }
    if (startDate) {
      return { startDate, endDate: dateKey(new Date()) };
    }
    if (endDate) {
      return { startDate: attestationQueryRange.startDate, endDate };
    }
    return attestationQueryRange;
  }, [startDate, endDate, attestationQueryRange]);

  const fetchClassifiedHours = useCallback(async () => {
    if (isSuperAdmin) {
      return;
    }
    try {
      setClassifiedLoading(true);
      const params = new URLSearchParams({
        startDate: classifiedRange.startDate,
        endDate: classifiedRange.endDate
      });
      if (isAdmin && filterUserId) {
        params.set('userId', filterUserId);
      }
      const response = await axios.get(`/api/time-report/classified-hours?${params.toString()}`);
      setClassifiedHours(response.data.data || null);
      setClassifiedError('');
    } catch (fetchError) {
      console.error('Error fetching classified hours:', fetchError);
      setClassifiedError(formatError(fetchError, t('failedFetchClassified')));
      setClassifiedHours(null);
    } finally {
      setClassifiedLoading(false);
    }
  }, [classifiedRange, filterUserId, isAdmin, isSuperAdmin]);

  const fetchEntries = useCallback(async () => {
    if (isSuperAdmin) {
      return;
    }
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (isAdmin && filterUserId) params.set('userId', filterUserId);

      const attestationParams = new URLSearchParams({
        startDate: attestationQueryRange.startDate,
        endDate: attestationQueryRange.endDate
      });
      if (isAdmin && filterUserId) {
        attestationParams.set('userId', filterUserId);
      }

      const query = params.toString();
      const [taskResponse, attendanceResponse, attestationResponse] = await Promise.all([
        axios.get(`/api/check-ins${query ? `?${query}` : ''}`),
        axios.get(`/api/day-attendance${query ? `?${query}` : ''}`),
        axios.get(`/api/time-report/attestation?${attestationParams.toString()}`)
      ]);
      setEntries(taskResponse.data.data || []);
      setAttendanceEntries(attendanceResponse.data.data || []);
      setAttestation(attestationResponse.data.data || { attested: [], unlocks: [] });
      setSegmentPickerRow(null);
      setError('');
    } catch (fetchError) {
      console.error('Error fetching check-ins:', fetchError);
      setError(formatError(fetchError, t('failedFetchReport')));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, filterUserId, isAdmin, isSuperAdmin, attestationQueryRange]);

  const refreshTimeReport = useCallback(async () => {
    await fetchEntries();
    await fetchClassifiedHours();
  }, [fetchEntries, fetchClassifiedHours]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    fetchClassifiedHours();
  }, [fetchClassifiedHours]);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    const loadUsers = async () => {
      try {
        const companyId = user?.active_company_id || user?.company_id;
        const users = await fetchCompanyUsers(companyId, user);
        setCompanyUsers(users);
      } catch (loadError) {
        console.error('Error fetching users:', loadError);
      }
    };

    loadUsers();
  }, [isAdmin, user]);

  const attestedDates = useMemo(
    () => (attestation.attested || []).map((row) => row.date),
    [attestation]
  );

  const unlockKeySet = useMemo(
    () => new Set((attestation.unlocks || []).map((row) => `${row.user_id}:${row.date}`)),
    [attestation]
  );

  const attestedByDate = useMemo(() => {
    const map = new Map();
    (attestation.attested || []).forEach((row) => {
      map.set(row.date, row);
    });
    return map;
  }, [attestation]);

  const isLockedForUser = useCallback(
    (userId, day) => attestedByDate.has(day) && !unlockKeySet.has(`${userId}:${day}`),
    [attestedByDate, unlockKeySet]
  );

  const selectedDayAttestation = attestedByDate.get(selectedDay) || null;
  const unlockTargetUserId = isAdmin
    ? (filterUserId ? Number(filterUserId) : null)
    : user?.id;
  const selectedDayLocked = selectedDayAttestation
    && unlockTargetUserId
    && isLockedForUser(unlockTargetUserId, selectedDay);
  const selectedDayUnlock = unlockTargetUserId
    ? (attestation.unlocks || []).find(
      (row) => row.date === selectedDay && row.user_id === unlockTargetUserId
    )
    : null;

  const openEditModal = (entry) => {
    setSegmentPickerRow(null);
    setEditingEntry(entry);
    setEditForm({
      check_in_time: toDateTimeLocalValue(entry.check_in_time),
      check_out_time: toDateTimeLocalValue(entry.check_out_time),
      time_spent: entry.time_spent ?? '',
      took_lunch: (entry.lunch_minutes || 0) > 0,
      lunch_minutes: entry.lunch_minutes > 0 ? String(entry.lunch_minutes) : ''
    });
    setError('');
  };

  const openTaskTimeEdit = (row) => {
    const editable = editableTaskTimeSegments(row, isLockedForUser);
    if (editable.length === 0) {
      return;
    }
    if (editable.length === 1) {
      openEditModal(editable[0]);
      return;
    }
    setEditingEntry(null);
    setSegmentPickerRow(row);
    setError('');
  };

  const closeEditModal = () => {
    setEditingEntry(null);
    setSubmitting(false);
  };

  const closeSegmentPicker = () => {
    setSegmentPickerRow(null);
  };

  const openAttendanceModal = (entry, checkout = false) => {
    setEditingAttendance({ ...entry, checkout });
    setHandoutsBlockingCheckout(false);
    setAttendanceForm({
      check_in_at: toDateTimeLocalValue(entry.check_in_at),
      check_out_at: toDateTimeLocalValue(checkout ? new Date() : entry.check_out_at),
      note: entry.note || '',
      took_lunch: (entry.lunch_minutes || 0) > 0,
      lunch_minutes: entry.lunch_minutes > 0 ? String(entry.lunch_minutes) : ''
    });
    setError('');
  };

  const closeAttendanceModal = () => {
    setEditingAttendance(null);
    setHandoutsBlockingCheckout(false);
  };

  const handleAttendanceCheckIn = async (sessionKind = 'ordinary') => {
    try {
      setSubmitting(true);
      await axios.post('/api/day-attendance/check-in', { session_kind: sessionKind });
      await refreshTimeReport();
    } catch (saveError) {
      setError(formatError(saveError, t('failedCheckIn')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAttendance = async (event) => {
    event.preventDefault();
    if (!editingAttendance) return;
    if (showEodHandoutsOnCheckout && handoutsBlockingCheckout) {
      setError(t('eodReturnHandoutsFirst', { ns: 'dashboard' }));
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        check_in_at: fromDateTimeLocalValue(attendanceForm.check_in_at),
        check_out_at: fromDateTimeLocalValue(attendanceForm.check_out_at),
        note: attendanceForm.note,
        lunch_minutes: attendanceForm.took_lunch ? Number.parseInt(attendanceForm.lunch_minutes, 10) : 0
      };
      const endpoint = editingAttendance.checkout
        ? `/api/day-attendance/${editingAttendance.id}/check-out`
        : `/api/day-attendance/${editingAttendance.id}`;
      await axios.put(endpoint, payload);
      closeAttendanceModal();
      await refreshTimeReport();
    } catch (saveError) {
      setError(formatError(saveError, t('failedUpdateAttendance')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();
    if (!editingEntry) return;

    try {
      setSubmitting(true);
      setError('');
      const checkInIso = fromDateTimeLocalValue(editForm.check_in_time);
      const checkOutIso = fromDateTimeLocalValue(editForm.check_out_time);
      const lunchMinutes = editForm.took_lunch
        ? Number.parseInt(editForm.lunch_minutes, 10)
        : 0;
      const timesUnchanged =
        checkInIso === new Date(editingEntry.check_in_time).toISOString()
        && checkOutIso === new Date(editingEntry.check_out_time).toISOString();
      const lunchUnchanged = lunchMinutes === (editingEntry.lunch_minutes || 0);
      const timeSpentChanged = Number(editForm.time_spent) !== editingEntry.time_spent;

      const payload = timesUnchanged && lunchUnchanged && timeSpentChanged
        ? { time_spent: Number(editForm.time_spent) }
        : {
          check_in_time: checkInIso,
          check_out_time: checkOutIso,
          lunch_minutes: lunchMinutes
        };

      await axios.put(`/api/check-ins/${editingEntry.id}`, payload);
      closeEditModal();
      await refreshTimeReport();
    } catch (saveError) {
      setError(formatError(saveError, t('failedUpdateEntry')));
      setSubmitting(false);
    }
  };

  const handleAttest = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      setError('');
      await axios.post('/api/time-report/attest', {
        startDate: attestForm.startDate,
        endDate: attestForm.endDate
      });
      await refreshTimeReport();
    } catch (attestError) {
      setError(formatError(attestError, t('failedAttest')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnlock = async (userId, day) => {
    try {
      setSubmitting(true);
      setError('');
      await axios.post('/api/time-report/unlock', {
        userId,
        date: day
      });
      await refreshTimeReport();
    } catch (unlockError) {
      setError(formatError(unlockError, t('failedUnlock')));
    } finally {
      setSubmitting(false);
    }
  };

  const exportJson = () => {
    const payload = {
      filter: { startDate: startDate || null, endDate: endDate || null, userId: filterUserId || null },
      taskTime: entries,
      dayAttendance: attendanceEntries,
      attestation,
      classifiedHours: classifiedHours || null
    };
    downloadTextFile(
      `time_report_${new Date().toISOString().split('T')[0]}.json`,
      JSON.stringify(payload, null, 2),
      'application/json;charset=utf-8;'
    );
  };

  const exportPayrollCsv = async () => {
    if (!startDate || !endDate) {
      setError(t('selectDatesExport'));
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const params = new URLSearchParams({ startDate, endDate });
      const response = await axios.get(`/api/time-report/payroll-export?${params.toString()}`);
      const exportData = response.data?.data || {};
      const rows = exportData.rows || [];
      if (rows.length === 0) {
        setError(t('noPayrollHours'));
        return;
      }

      const typeSlugs = exportData.typeSlugs || [];
      const baseHeaders = ['name', 'email', 'date', 'hours', 'note'];
      const typeHeaders = typeSlugs.map((slug) => `${slug}_hours`);
      const headers = [...baseHeaders, ...typeHeaders];
      const csvContent = [
        headers.join(','),
        ...rows.map((row) =>
          [
            ...baseHeaders.map((key) => escapeCsvValue(row[key])),
            ...typeSlugs.map((slug) => escapeCsvValue(row.hoursByType?.[slug] ?? ''))
          ].join(',')
        )
      ].join('\n');

      downloadTextFile(
        `payroll_hours_${startDate}_${endDate}.csv`,
        csvContent,
        'text/csv;charset=utf-8;'
      );
    } catch (exportError) {
      setError(formatError(exportError));
    } finally {
      setSubmitting(false);
    }
  };

  const pushFortnoxHours = async () => {
    if (!startDate || !endDate) {
      setError(t('selectDatesFortnox'));
      return;
    }

    if (!(await confirm(t('confirmFortnoxPush', { startDate, endDate }), { danger: true }))) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setFortnoxPushSummary(null);
      const response = await axios.post('/api/integrations/fortnox/push', {
        startDate,
        endDate
      });
      setFortnoxPushSummary(response.data?.data || null);
    } catch (pushError) {
      setError(formatError(pushError));
    } finally {
      setSubmitting(false);
    }
  };

  const taskTimeRows = useMemo(() => aggregateTaskTimeEntries(entries), [entries]);
  const completedCount = useMemo(
    () => taskTimeRows.filter((entry) => entry.check_out_time).length,
    [taskTimeRows]
  );
  const attendanceCompletedCount = useMemo(
    () => attendanceEntries.filter((entry) => entry.check_out_at).length,
    [attendanceEntries]
  );
  const attendanceTotal = useMemo(
    () => attendanceEntries.reduce((total, entry) => total + (entry.duration_minutes || 0), 0),
    [attendanceEntries]
  );
  const openAttendanceSession = attendanceEntries.find((entry) => !entry.check_out_at);
  const selectedDaySessions = attendanceEntries.filter((entry) => dateKey(entry.check_in_at) === selectedDay);

  if (isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading && entries.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="time-report">
      <div className="container container--wide main-content">
        <div className="page-header time-report-header">
          <div>
            <h1>{t('title')}</h1>
            <p className="time-report-subtitle">
              {t('subtitle')}
            </p>
          </div>
          {(canExport || canPushFortnox || isAdmin) && (
            <div className="time-report-export-actions">
              <div className="time-report-export-actions-desktop">
                {canExport && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={exportPayrollCsv}
                    disabled={submitting}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">table</span>
                    {t('exportPayrollCsv')}
                  </button>
                )}
                {canPushFortnox && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={pushFortnoxHours}
                    disabled={submitting}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">sync</span>
                    {t('pushFortnox')}
                  </button>
                )}
                {canExport && (
                  <button type="button" className="btn btn-secondary" onClick={exportJson}>
                    <span className="material-symbols-outlined" aria-hidden="true">download</span>
                    {t('exportJson')}
                  </button>
                )}
                {isAdmin && (
                  <Link to="/work-hour-rules" className="btn btn-secondary">
                    <span className="material-symbols-outlined" aria-hidden="true">tune</span>
                    {t('configureHourRules')}
                  </Link>
                )}
              </div>
              <div className="time-report-export-actions-mobile">
                {canExport && (
                  <button type="button" className="btn btn-secondary" onClick={exportJson}>
                    <span className="material-symbols-outlined" aria-hidden="true">download</span>
                    {t('exportJson')}
                  </button>
                )}
                {(canExport || canPushFortnox) ? (
                  <details className="time-report-more-actions" ref={moreActionsRef}>
                    <summary className="btn btn-secondary">{t('moreActions')}</summary>
                    <div className="time-report-more-actions-menu" role="group" aria-label={t('moreActions')}>
                      {canExport && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            closeMoreActions();
                            exportPayrollCsv();
                          }}
                          disabled={submitting}
                        >
                          {t('exportPayrollCsv')}
                        </button>
                      )}
                      {canPushFortnox && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            closeMoreActions();
                            pushFortnoxHours();
                          }}
                          disabled={submitting}
                        >
                          {t('pushFortnox')}
                        </button>
                      )}
                      {isAdmin && (
                        <Link
                          to="/work-hour-rules"
                          className="btn btn-secondary"
                          onClick={closeMoreActions}
                        >
                          {t('configureHourRules')}
                        </Link>
                      )}
                    </div>
                  </details>
                ) : isAdmin ? (
                  <Link to="/work-hour-rules" className="btn btn-secondary">
                    <span className="material-symbols-outlined" aria-hidden="true">tune</span>
                    {t('configureHourRules')}
                  </Link>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {error && !editingEntry && !editingAttendance && <div className="error">{error}</div>}
        {fortnoxPushSummary && (
          <div className="success time-report-fortnox-summary" role="status">
            {t('fortnoxPushSummary', {
              sent: fortnoxPushSummary.summary?.sent || 0,
              skipped: fortnoxPushSummary.summary?.skipped || 0,
              errors: fortnoxPushSummary.summary?.errors || 0
            })}
            {Array.isArray(fortnoxPushSummary.skipped) && fortnoxPushSummary.skipped.length > 0 && (
              <ul>
                {fortnoxPushSummary.skipped.slice(0, 8).map((item) => (
                  <li key={`skip-${item.userId}-${item.date}-${item.hourType || 'all'}`}>
                    {item.name || item.email || t('userFallback', { id: item.userId })}
                    {' '}
                    ({item.date}{item.hourType ? `, ${item.hourType}` : ''}):
                    {' '}
                    {item.reason === 'unmapped'
                      ? t('unmappedEmployee')
                      : item.reason === 'unmapped_hour_type'
                        ? item.message || t('unmappedHourType')
                        : item.message || item.reason || t('skipped')}
                  </li>
                ))}
              </ul>
            )}
            {Array.isArray(fortnoxPushSummary.errors) && fortnoxPushSummary.errors.length > 0 && (
              <ul>
                {fortnoxPushSummary.errors.slice(0, 8).map((item) => (
                  <li key={`err-${item.userId}-${item.date}`}>
                    {item.name || item.email || t('userFallback', { id: item.userId })}
                    {' '}
                    ({item.date}):
                    {' '}
                    {item.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="card time-report-filters">
          <div className="time-report-filter-row">
            <div>
              <label htmlFor="time-report-start">{t('startDate')}</label>
              <input
                id="time-report-start"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </div>
            <div>
              <label htmlFor="time-report-end">{t('endDate')}</label>
              <input
                id="time-report-end"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
              />
            </div>
            {isAdmin && (
              <div>
                <label htmlFor="time-report-user">{t('userFilter')}</label>
                <select
                  id="time-report-user"
                  value={filterUserId}
                  onChange={(event) => setFilterUserId(event.target.value)}
                >
                  <option value="">{t('allUsers')}</option>
                  {companyUsers.map((companyUser) => (
                    <option key={companyUser.id} value={companyUser.id}>
                      {formatUserLabel(companyUser)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <p className="time-report-meta">
            {t('taskTimeMeta', { count: taskTimeRows.length, completed: completedCount })}
          </p>
        </div>

        {isAdmin && (
          <>
            <hr className="tasks-section-divider" aria-hidden="true" />
            <section className="tasks-section time-report-section">
              <div className="tasks-section-header time-report-section-header">
                <div>
                  <h2 className="time-report-section-title">{t('attestPeriodTitle')}</h2>
                  <p className="time-report-meta">
                    {t('attestPeriodHelp')}
                  </p>
                </div>
              </div>
              <div className="card time-report-attest-card">
                <form className="time-report-filter-row" onSubmit={handleAttest}>
                  <div>
                    <label htmlFor="attest-start">{t('startDate')}</label>
                    <input
                      id="attest-start"
                      type="date"
                      value={attestForm.startDate}
                      max={yesterdayKey()}
                      onChange={(event) => setAttestForm((prev) => ({ ...prev, startDate: event.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="attest-end">{t('endDate')}</label>
                    <input
                      id="attest-end"
                      type="date"
                      value={attestForm.endDate}
                      max={yesterdayKey()}
                      onChange={(event) => setAttestForm((prev) => ({ ...prev, endDate: event.target.value }))}
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? t('attesting') : t('attestPeriod')}
                  </button>
                </form>
              </div>
            </section>
          </>
        )}

        <hr className="tasks-section-divider" aria-hidden="true" />
        <section className="tasks-section time-report-section">
          <div className="tasks-section-header time-report-section-header">
            <div>
              <h2 className="time-report-section-title">{t('classifiedHoursTitle')}</h2>
              <p className="time-report-meta">
                {t('classifiedHoursMeta', {
                  startDate: classifiedRange.startDate,
                  endDate: classifiedRange.endDate
                })}
              </p>
            </div>
          </div>
          <div className="card time-report-table-card">
            <ClassifiedHoursPanel
              data={classifiedHours}
              loading={classifiedLoading}
              error={classifiedError}
              selectedDay={selectedDay}
              showMultipleUsers={isAdmin && !filterUserId}
            />
          </div>
        </section>

        <hr className="tasks-section-divider" aria-hidden="true" />
        <section className="tasks-section time-report-section">
          <div className="tasks-section-header time-report-section-header">
            <div>
              <h2 className="time-report-section-title">{t('dayAttendanceTitle')}</h2>
              <p className="time-report-meta">
                {t('sessionsMeta', { count: attendanceEntries.length })}
                {attendanceCompletedCount !== attendanceEntries.length
                  ? t('sessionsCompletedSuffix', { completed: attendanceCompletedCount })
                  : ''}
                {' · '}{t('completedTime', { time: formatTime(attendanceTotal) })}
              </p>
            </div>
            {openAttendanceSession ? (
              <button type="button" className="btn btn-primary" onClick={() => openAttendanceModal(openAttendanceSession, true)}>
                {openAttendanceSession.session_kind === 'jour'
                  ? t('checkOutJour', { ns: 'dashboard' })
                  : t('checkOut')}
              </button>
            ) : (
              <div className="time-report-attendance-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleAttendanceCheckIn('ordinary')}
                  disabled={submitting}
                >
                  {t('checkIn')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleAttendanceCheckIn('jour')}
                  disabled={submitting}
                >
                  {t('checkInJour', { ns: 'dashboard' })}
                </button>
              </div>
            )}
          </div>
          <div className="time-report-attendance-day">
            <AttendanceCalendar
              sessions={attendanceEntries}
              attestedDates={attestedDates}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              selectedDay={selectedDay}
              onSelectDay={setSelectedDay}
            />
            <div className="card time-report-table-card">
              <h3 className="attendance-selected-day-title">{t('sessionsOnDate', { date: selectedDay })}</h3>
              {selectedDayAttestation && (
                <div className="time-report-attestation-banner">
                  <p>
                    {t('attestedBy', {
                      name: selectedDayAttestation.attested_by_name || t('anAdmin'),
                      datetime: formatDateTime(selectedDayAttestation.attested_at)
                    })}
                  </p>
                  {selectedDayUnlock && (
                    <p className="time-report-meta">
                      {t('unlockedBy', {
                        name: selectedDayUnlock.unlocked_by_name || t('anAdmin'),
                        datetime: formatDateTime(selectedDayUnlock.unlocked_at)
                      })}
                    </p>
                  )}
                  {selectedDayLocked ? (
                    <p className="time-report-lock-message">
                      {isAdmin ? t('lockedAdminMessage') : t('lockedEmployeeMessage')}
                    </p>
                  ) : null}
                  {isAdmin && selectedDayLocked && unlockTargetUserId && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleUnlock(unlockTargetUserId, selectedDay)}
                      disabled={submitting}
                    >
                      {t('unlockSelectedUser')}
                    </button>
                  )}
                  {isAdmin && selectedDayAttestation && !filterUserId && (
                    <p className="time-report-meta">
                      {t('unlockFilterHint')}
                    </p>
                  )}
                </div>
              )}
              {selectedDaySessions.length === 0 ? (
                <TimeReportEmpty
                  description={
                    selectedDayAttestation
                      ? t('noSessionsAttestedDate')
                      : t('noSessionsDate')
                  }
                />
              ) : (
                <AttendanceTable
                  entries={selectedDaySessions}
                  isAdmin={isAdmin}
                  isLockedForUser={isLockedForUser}
                  onEdit={openAttendanceModal}
                  onUnlock={handleUnlock}
                  submitting={submitting}
                />
              )}
            </div>
          </div>
          <div className="card time-report-table-card">
            <h3 className="attendance-selected-day-title time-report-subsection-title">{t('dateRangeSessions')}</h3>
            {attendanceEntries.length === 0 ? (
              <TimeReportEmpty description={t('noSessionsFilter')} />
            ) : (
              <AttendanceTable
                entries={attendanceEntries}
                isAdmin={isAdmin}
                isLockedForUser={isLockedForUser}
                onEdit={openAttendanceModal}
                onUnlock={handleUnlock}
                submitting={submitting}
              />
            )}
          </div>
        </section>

        <hr className="tasks-section-divider" aria-hidden="true" />
        <section className="tasks-section time-report-section">
          <div className="tasks-section-header time-report-section-header">
            <h2 className="time-report-section-title">{t('taskTimeTitle')}</h2>
          </div>
          <div className="card time-report-table-card">
            {taskTimeRows.length === 0 ? (
              <TimeReportEmpty description={t('noCheckInsFilter')} />
            ) : (
              <>
                <div className="time-report-table-desktop">
                  <div className="table-responsive">
                    <table className="time-report-table">
                      <thead>
                        <tr>
                          <th>{t('columnTask')}</th>
                          {isAdmin && <th>{t('columnUser')}</th>}
                          <th>{t('columnCheckIn')}</th>
                          <th>{t('columnCheckOut')}</th>
                          <th>{t('columnTimeSpent')}</th>
                          <th>{t('columnSummary')}</th>
                          <th aria-label={t('columnActions')} />
                        </tr>
                      </thead>
                      <tbody>
                        {taskTimeRows.map((entry) => {
                          const isCompleted = Boolean(entry.check_out_time);
                          const editable = editableTaskTimeSegments(entry, isLockedForUser);
                          const unlockDay = firstLockedTaskTimeDay(entry, isLockedForUser);
                          return (
                            <tr key={entry.rowKey}>
                              <td>
                                <Link to={`/tasks/${entry.task_id}`}>{entry.task_title}</Link>
                              </td>
                              {isAdmin && <td>{entry.user_name}</td>}
                              <td>{formatDateTime(entry.check_in_time) || '—'}</td>
                              <td>{formatDateTime(entry.check_out_time) || '—'}</td>
                              <td>{formatTime(entry.time_spent)}</td>
                              <td className="time-report-summary">
                                {[
                                  entry.segmentCount > 1
                                    ? t('segmentCount', { count: entry.segmentCount })
                                    : null,
                                  entry.summary,
                                  entry.lunch_minutes > 0
                                    ? t('lunchInNote', { minutes: entry.lunch_minutes })
                                    : null
                                ]
                                  .filter(Boolean)
                                  .join(' · ') || '—'}
                              </td>
                              <td>
                                {!isCompleted ? (
                                  <span className="time-report-status-pill time-report-status-pill--active">{t('active')}</span>
                                ) : unlockDay ? (
                                  <span className="time-report-status-pill time-report-status-pill--attested">{t('attested')}</span>
                                ) : null}
                                {(unlockDay || editable.length > 0) && (
                                  <div className="time-report-lock-actions">
                                    {unlockDay && isAdmin && (
                                      <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => handleUnlock(entry.user_id, unlockDay)}
                                        disabled={submitting}
                                      >
                                        {t('unlock')}
                                      </button>
                                    )}
                                    {editable.length > 0 && (
                                      <button
                                        type="button"
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => openTaskTimeEdit(entry)}
                                      >
                                        {t('edit')}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <TaskTimeCards
                  entries={taskTimeRows}
                  isAdmin={isAdmin}
                  isLockedForUser={isLockedForUser}
                  onEdit={openTaskTimeEdit}
                  onUnlock={handleUnlock}
                  submitting={submitting}
                />
              </>
            )}
          </div>
        </section>
      </div>

      <Modal
        isOpen={!!segmentPickerRow}
        onClose={closeSegmentPicker}
        title={t('chooseSegmentTitle')}
        ariaLabelledBy="time-report-segment-picker-title"
      >
        <p className="time-report-modal-task">{segmentPickerRow?.task_title}</p>
        <p className="time-report-meta">{t('chooseSegmentHelp')}</p>
        <ul className="time-report-segment-picker-list">
          {editableTaskTimeSegments(segmentPickerRow || { segments: [] }, isLockedForUser).map((segment) => (
            <li key={segment.id}>
              <button
                type="button"
                className="time-report-segment-picker-item"
                onClick={() => openEditModal(segment)}
              >
                <span>
                  {formatDateTime(segment.check_in_time) || '—'}
                  {' → '}
                  {formatDateTime(segment.check_out_time) || '—'}
                </span>
                <span>{formatTime(segmentNetMinutes(segment))}</span>
              </button>
            </li>
          ))}
        </ul>
        <div className="modal-actions time-report-modal-actions">
          <button type="button" className="btn btn-secondary" onClick={closeSegmentPicker}>
            {t('cancel', { ns: 'common' })}
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={!!editingEntry}
        onClose={closeEditModal}
        title={t('editTimeEntryTitle')}
        ariaLabelledBy="time-report-edit-title"
      >
        <p className="time-report-modal-task">{editingEntry?.task_title}</p>
        {error && <div className="error">{error}</div>}
        <form onSubmit={handleSaveEdit}>
          <div className="time-report-form-field">
            <label htmlFor="edit-check-in">{t('columnCheckIn')}</label>
            <input
              id="edit-check-in"
              type="datetime-local"
              value={editForm.check_in_time}
              onChange={(event) =>
                setEditForm((prev) => ({ ...prev, check_in_time: event.target.value }))
              }
              required
            />
          </div>
          <div className="time-report-form-field">
            <label htmlFor="edit-check-out">{t('columnCheckOut')}</label>
            <input
              id="edit-check-out"
              type="datetime-local"
              value={editForm.check_out_time}
              onChange={(event) =>
                setEditForm((prev) => ({ ...prev, check_out_time: event.target.value }))
              }
              required
            />
          </div>
          <div className="time-report-form-field">
            <label htmlFor="edit-time-spent">{t('columnTimeSpent')}</label>
            <input
              id="edit-time-spent"
              type="number"
              min="0"
              value={editForm.time_spent}
              onChange={(event) =>
                setEditForm((prev) => ({ ...prev, time_spent: event.target.value }))
              }
              required
            />
          </div>
          <label className="time-report-checkbox">
            <input
              type="checkbox"
              checked={editForm.took_lunch}
              onChange={(event) => setEditForm((prev) => ({
                ...prev,
                took_lunch: event.target.checked,
                lunch_minutes: event.target.checked ? prev.lunch_minutes || '30' : ''
              }))}
            />
            {t('tookLunchBreak', { ns: 'dashboard' })}
          </label>
          {editForm.took_lunch && (
            <div className="time-report-form-field">
              <label htmlFor="edit-lunch-minutes">{t('lunchDurationMinutes', { ns: 'dashboard' })}</label>
              <input
                id="edit-lunch-minutes"
                type="number"
                min="1"
                step="1"
                value={editForm.lunch_minutes}
                onChange={(event) =>
                  setEditForm((prev) => ({ ...prev, lunch_minutes: event.target.value }))
                }
                required
              />
            </div>
          )}
          <div className="modal-actions time-report-modal-actions">
            <button type="button" className="btn btn-secondary" onClick={closeEditModal}>
              {t('cancel', { ns: 'common' })}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t('saving') : t('saveChanges')}
            </button>
          </div>
        </form>
      </Modal>
      <Modal
        isOpen={!!editingAttendance}
        onClose={closeAttendanceModal}
        title={
          editingAttendance?.checkout
            ? (editingAttendance.session_kind === 'jour'
              ? t('checkoutModalTitleJour', { ns: 'dashboard' })
              : t('checkoutDayAttendanceTitle'))
            : t('editDayAttendanceTitle')
        }
      >
        {error && <div className="error">{error}</div>}
        <form onSubmit={handleSaveAttendance}>
          <div className="time-report-form-field">
            <label htmlFor="attendance-check-in">{t('columnCheckIn')}</label>
            <input id="attendance-check-in" type="datetime-local" value={attendanceForm.check_in_at} onChange={(event) => setAttendanceForm((form) => ({ ...form, check_in_at: event.target.value }))} required />
          </div>
          <div className="time-report-form-field">
            <label htmlFor="attendance-check-out">{t('columnCheckOut')}</label>
            <input id="attendance-check-out" type="datetime-local" value={attendanceForm.check_out_at} onChange={(event) => setAttendanceForm((form) => ({ ...form, check_out_at: event.target.value }))} required />
          </div>
          <div className="time-report-form-field">
            <label htmlFor="attendance-note">{t('noteOptional')}</label>
            <textarea id="attendance-note" value={attendanceForm.note} onChange={(event) => setAttendanceForm((form) => ({ ...form, note: event.target.value }))} />
          </div>
          <div className="time-report-form-field">
            <label className="time-report-checkbox-label">
              <input
                type="checkbox"
                checked={attendanceForm.took_lunch}
                onChange={(event) => setAttendanceForm((form) => ({
                  ...form,
                  took_lunch: event.target.checked,
                  lunch_minutes: event.target.checked ? form.lunch_minutes || '30' : ''
                }))}
              />
              {t('tookLunchBreak')}
            </label>
          </div>
          {attendanceForm.took_lunch && (
            <div className="time-report-form-field">
              <label htmlFor="attendance-lunch-minutes">{t('lunchDurationMinutes')}</label>
              <input
                id="attendance-lunch-minutes"
                type="number"
                min="1"
                step="1"
                value={attendanceForm.lunch_minutes}
                onChange={(event) => setAttendanceForm((form) => ({ ...form, lunch_minutes: event.target.value }))}
                required
              />
            </div>
          )}
          {showEodHandoutsOnCheckout && (
            <EndOfDayHandoutsSection
              active={showEodHandoutsOnCheckout}
              user={user}
              onBlockingChange={setHandoutsBlockingCheckout}
              onError={(message) => setError(message)}
            />
          )}
          <div className="modal-actions time-report-modal-actions">
            <button type="button" className="btn btn-secondary" onClick={closeAttendanceModal}>{t('cancel', { ns: 'common' })}</button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || (showEodHandoutsOnCheckout && handoutsBlockingCheckout)}
            >
              {submitting
                ? t('saving')
                : (editingAttendance?.checkout ? t('checkOutForDay', { ns: 'dashboard' }) : t('saveChanges'))}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default TimeReport;
